import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowDropDown,
  DoneAll,
  PersonOutline as Person,
  DarkModeOutlined,
  LightModeOutlined,
} from "@mui/icons-material";
import FlexBetween from "../components/FlexBetween";
import velocityLogo from "../assets/logo-light-v2.svg";
import xlr8LogoSmall from "../assets/svgs/xlr8_logo_small.svg";
import {
  AppBar,
  Button,
  Box,
  Typography,
  IconButton,
  Toolbar,
  Menu,
  MenuItem,
  Badge,
  Avatar,
  Popover,
  List,
  ListItem,
  ListItemText,
  Tooltip,
} from "@mui/material";
import { useAuth } from "../contexts/AuthContext";
import { useNotifications } from "../contexts/NotificationContext";
import { useThemeMode } from "../contexts/ThemeModeContext";
import { darkModeTokens } from "../styles/Theme";
import {
  IconDownloadBlueSvg,
  IconLogoutNew,
  IconNotification,
} from "../common/utils/icons";
import {
  extractFileAndDirectoriesName,
  handleSecureDownload,
} from "../services/utils";

interface NavbarProps {
  user: {
    name: string;
    occupation: string;
  };
  isSidebarOpen: boolean;
  setIsSidebarOpen: (isOpen: boolean) => void;
  // DQ-8763: SidebarV2 owns the product mark, so the V2 shell hides it here and
  // right-aligns the controls. The legacy sidebar has no logo header of its own,
  // so the REACT_APP_SIDEBAR_V2=false path keeps rendering it.
  showLogo?: boolean;
}

const Navbar: React.FC<NavbarProps> = ({ isSidebarOpen, showLogo = true }) => {
  const navigate = useNavigate();
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const isOpen = Boolean(anchorEl);
  const handleClick = (event: React.MouseEvent<HTMLElement>) =>
    setAnchorEl(event.currentTarget);
  const handleClose = () => setAnchorEl(null);
  const { logout } = useAuth();
  const [notificationAnchorEl, setNotificationAnchorEl] =
    useState<null | HTMLElement>(null);

  const { userInfo } = useAuth();
  const { firstname = "", lastname = "", username = "" } = userInfo || {};
  const { mode, toggleMode } = useThemeMode();
  const isDark = mode === "dark";
  // Use the notification context
  const {
    notifications,
    markAsRead,
    markAllAsRead,
    clearAllNotifications,
    unreadCount,
  } = useNotifications();

  const handleNotificationClick = (event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
  };

  const handleNotificationClose = () => {
    setNotificationAnchorEl(null);
  };

  const handleNotificationItemClick = (notification: any) => {
    // Extract executionId if it exists

    // Mark the notification as read
    markAsRead(notification.id);

    if (
      notification?.job_status === "completed" &&
      notification?.execution_id
    ) {
      const { execution_id, execution_type } = notification;

      const paths: any = {
        validation: `/validation-execution-history/validate-result/${execution_id}`,
        validation_rerun: `/validation-execution-history/validate-result/${execution_id}`,
        rule: `/rules-execution-history/${execution_id}/${execution_id}/dashboard`,
        rule_rerun: `/rules-execution-history/${execution_id}/${execution_id}/dashboard`,
      };

      if (paths[execution_type]) {
        navigate(paths[execution_type]);
      }
    }

    handleNotificationClose();
  };

  const handleMarkAllAsRead = () => {
    markAllAsRead();
  };

  const handleClearAllNotifications = () => {
    clearAllNotifications();
    handleNotificationClose();
  };

  const handleLogout = () => {
    // Delegate cleanup to logout() and global guards
    logout();
    navigate("/login");
    handleClose();
  };

  const getLogFilePath = (path: any) => {
    const parts = extractFileAndDirectoriesName(path);
    let filepath = `${parts[parts.length - 2]}/${parts[parts.length - 1]}`;
    return { filepath, fileName: parts[parts.length - 1] };
  };
  const downloadLogFile = (logFile: any) => {
    if (logFile === null || logFile === undefined) {
      return;
    }
    const { filepath, fileName }: any = getLogFilePath(logFile);
    handleSecureDownload(filepath, fileName);
  };

  return (
    <AppBar
      sx={{
        position: "static",
        background: "none",
        // Dark mode: add a 1-px bottom separator so the header edge is visible
        // against the dark page surface without relying on a drop-shadow.
        // The SCSS class .main-header overrides `background` with !important,
        // so the background is fully controlled by the CSS variable cascade
        // (--card-bg → #151722 in dark mode).  boxShadow is not set in SCSS,
        // so this override is safe.
        boxShadow: isDark ? `0 1px 0 ${darkModeTokens.border}` : "none",
      }}
      className="main-header"
    >
      <Toolbar
        sx={{ justifyContent: showLogo ? "space-between" : "flex-end" }}
      >
        {/* LEFT SIDE */}
        {showLogo && (
          <Box className="logo-with-searchbar">
            <Box className="sidebarLogoBox">
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                sx={{ columnGap: "20px" }}
              >
                <img
                  src={isSidebarOpen ? velocityLogo : xlr8LogoSmall}
                  className={isSidebarOpen ? "large-logo" : "small-logo"}
                  alt={isSidebarOpen ? "Velocity Logo" : "AutoXLR8"}
                />
              </Box>
            </Box>
            {/* <FlexBetween className="search-panel">
              <IconButton className="search-icon">
                <Search className="svg_icons" />
              </IconButton>
              <InputBase placeholder="Search..." className="search-textbox" />
            </FlexBetween> */}
          </Box>
        )}

        {/* RIGHT SIDE */}
        <FlexBetween gap="1.5rem">
          <Tooltip
            title={
              mode === "light" ? "Switch to dark mode" : "Switch to light mode"
            }
          >
            <IconButton
              className="icon-theme-toggle"
              onClick={toggleMode}
              aria-label={
                mode === "light"
                  ? "Switch to dark mode"
                  : "Switch to light mode"
              }
              data-testid="theme-toggle-btn"
              // Always white: the navbar surface is brand.navy (#0E1223) in
              // both Light and Dark mode, so the icon must use --text-on-brand
              // (#ffffff) regardless of the active mode.
              sx={{ color: "var(--text-on-brand)" }}
            >
              {mode === "light" ? <DarkModeOutlined /> : <LightModeOutlined />}
            </IconButton>
          </Tooltip>

          <IconButton
            className="icon-notification"
            onClick={handleNotificationClick}
            aria-label={`Notifications ${
              unreadCount > 0 ? `(${unreadCount} unread)` : ""
            }`}
            title={`Notifications ${
              unreadCount > 0 ? `(${unreadCount} unread)` : ""
            }`}
            data-testid="notifications-btn"
          >
            <Badge badgeContent={unreadCount} color="error">
              <IconNotification />
            </Badge>
          </IconButton>

          <Popover
            open={Boolean(notificationAnchorEl)}
            anchorEl={notificationAnchorEl}
            onClose={handleNotificationClose}
            anchorOrigin={{
              vertical: "bottom",
              horizontal: "right",
            }}
            transformOrigin={{
              vertical: "top",
              horizontal: "right",
            }}
          >
            <Box sx={{ width: 300, maxHeight: 500 }}>
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                  padding: "8px 16px",
                  borderBottom: "1px solid",
                  borderBottomColor: "divider",
                }}
              >
                <Typography variant="subtitle1" fontWeight="bold">
                  Notifications
                </Typography>
                <Box>
                  <Tooltip title="Mark all as read">
                    <IconButton size="small" onClick={handleMarkAllAsRead}>
                      <DoneAll fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              </Box>

              <List className="notifications-list">
                {notifications.length === 0 ? (
                  <ListItem>
                    <ListItemText primary="No notifications" />
                  </ListItem>
                ) : (
                  notifications.map((notification, index) => (
                    <ListItem
                      sx={{
                        // DQ-8674: unread tint uses brand.blue 8-10% per the
                        // dark-mode token system.  The old value rgba(25,118,210,0.08)
                        // was the MUI Material default blue — not a brand token.
                        backgroundColor: notification.read
                          ? "transparent"
                          : isDark
                            ? darkModeTokens.rowHover // rgba(104,158,200,0.08) brand.blue 8%
                            : "rgba(104,158,200,0.08)", // same tint, light bg context
                      }}
                      onClick={() => handleNotificationItemClick(notification)}
                    >
                      <ListItemText
                        primary={<></>}
                        secondary={
                          <Box>
                            <Typography variant="body2" color="text.secondary">
                              {notification?.job_status === "failed" &&
                              notification?.log_file ? (
                                <Box className="with-download">
                                  <Tooltip
                                    key={notification.id || index}
                                    title={notification.message}
                                    placement="left"
                                    arrow
                                  >
                                    <Box className="message">
                                      {`${notification.message}`}
                                    </Box>
                                  </Tooltip>
                                  <Tooltip
                                    key={notification.id || index}
                                    title={"Download Log File"}
                                    placement="top"
                                    arrow
                                  >
                                    <Box
                                      className="download-icon"
                                      onClick={() =>
                                        downloadLogFile(notification?.log_file)
                                      }
                                    >
                                      <IconDownloadBlueSvg />
                                    </Box>
                                  </Tooltip>
                                </Box>
                              ) : (
                                <Tooltip
                                  key={notification.id || index}
                                  title={notification.message}
                                  placement="left"
                                  arrow
                                >
                                  <Box>{`${notification.message}`}</Box>
                                </Tooltip>
                              )}
                            </Typography>
                          </Box>
                        }
                        secondaryTypographyProps={{
                          sx: {
                            overflow: "hidden",
                            textOverflow: "ellipsis",
                            display: "-webkit-box",
                            WebkitLineClamp: 2,
                            WebkitBoxOrient: "vertical",
                          },
                        }}
                      />
                    </ListItem>
                  ))
                )}
              </List>

              {notifications.length > 0 && (
                <Box
                  sx={{
                    padding: "8px 16px",
                    borderTop: "1px solid",
                    borderTopColor: "divider",
                    display: "flex",
                    justifyContent: "center",
                  }}
                >
                  <Button
                    size="small"
                    color="error"
                    onClick={handleClearAllNotifications}
                  >
                    Clear All
                  </Button>
                </Box>
              )}
            </Box>
          </Popover>

          <FlexBetween>
            <Button onClick={handleClick} className={"user-dropdown"}>
              <Box textAlign="left">
                <Avatar className="username">
                  {firstname ? firstname.slice(0, 1) : ""}
                  {lastname ? lastname.slice(0, 1) : ""}
                </Avatar>
              </Box>
              <ArrowDropDown className="dropdown-arrow" />
            </Button>
            <Menu
              anchorEl={anchorEl}
              open={isOpen}
              onClose={handleClose}
              anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
              className="header-dropdown-menu"
            >
              {/* User Info Section */}
              <Box className="user-info-section">
                <Typography className="user-name">{username}</Typography>
              </Box>

              {/* Menu Items */}
              <MenuItem
                className="menu-item-custom"
                onClick={() => {
                  navigate("/profile");
                  handleClose();
                }}
              >
                <Person className="menu-icon" />
                <span>My Profile</span>
              </MenuItem>

              {/* Version */}
              <Box className="version-section">
                <Typography className="version-text">
                  UI Version: 26.02.0
                </Typography>
              </Box>

              {/* Logout */}
              <MenuItem className="logout-menu-item">
                <Button className="btn-logout-full" onClick={handleLogout}>
                  <IconLogoutNew />
                  Logout
                </Button>
              </MenuItem>
            </Menu>
          </FlexBetween>
        </FlexBetween>
      </Toolbar>
    </AppBar>
  );
};

export default Navbar;
